import { AuthError } from '@app-builder/components/Auth/AuthError';
import { authI18n } from '@app-builder/components/Auth/auth-i18n';
import { MfaChallenge } from '@app-builder/components/Auth/MfaChallenge';
import { SignInFirstConnection } from '@app-builder/components/Auth/SignInFirstConnection';
import { SignInWithEmailDiscovery } from '@app-builder/components/Auth/SignInWithEmailDiscovery';
import { SignInWithGoogle } from '@app-builder/components/Auth/SignInWithGoogle';
import { SignInWithMicrosoft } from '@app-builder/components/Auth/SignInWithMicrosoft';
import { UnreadyCallout } from '@app-builder/components/Auth/UnreadyCallout';
import { Spinner } from '@app-builder/components/Spinner';
import { servicesMiddleware } from '@app-builder/middlewares/services-middleware';
import { type AuthErrors } from '@app-builder/models/auth-errors';
import { getAuthMode } from '@app-builder/models/auth-mode';
import { signInFn } from '@app-builder/server-fns/auth';
import { type AuthPayload } from '@app-builder/services/auth/auth.server';
import { useAuthSession } from '@app-builder/services/auth/auth-session.server';
import {
  notifyOtherTabsToCheckSession,
  preserveSignedOut,
  signedOutPageSearch,
} from '@app-builder/utils/cross-tab-session';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, ErrorComponent, Link, redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { type MultiFactorResolver } from 'firebase/auth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CtaV2ClassName, Typo } from 'ui-design-system';
import { z } from 'zod/v4';

const signInLoader = createServerFn()
  .middleware([servicesMiddleware])
  .validator(z.object({ signedOut: z.boolean() }))
  .handler(async function signInLoader({ context, data: { signedOut } }) {
    const request = getRequest();
    const url = new URL(request.url);
    if (!signedOut) {
      try {
        await context.services.authService.isAuthenticated(request, {
          successRedirect: '/app-router',
        });
      } catch (error) {
        if (error instanceof Response && error.status >= 300 && error.status < 400) {
          throw redirect({ href: error.headers.get('Location')!, statusCode: error.status });
        }
        throw error;
      }
    }
    const appConfig = context.appConfig;

    // A migrated but empty instance has nothing to sign into yet: onboard first.
    if (appConfig?.status.migrations && !appConfig.status.hasOrg) {
      throw redirect({ to: '/onboarding' });
    }

    const authProviders = appConfig?.auth.providers ?? ['firebase'];
    const authMode = getAuthMode(authProviders);
    const isSsoEnabled = appConfig?.features.sso ?? false;
    const isEmailDiscoveryEnabled = isSsoEnabled && authMode === 'firebase-oidc';
    // Handle email parameter manually to preserve literal '+' characters
    const emailParam = url.searchParams.toString().match(/email=([^&]*)/)?.[1];
    const prefilledEmail = emailParam ? decodeURIComponent(emailParam.replace(/\+/g, '%2B')) : '';

    // `/sign-in-email` redirects back here without Firebase, so only send Firebase deployments there.
    if (
      authMode !== null &&
      !isEmailDiscoveryEnabled &&
      authProviders.includes('firebase') &&
      (!isSsoEnabled || prefilledEmail)
    ) {
      throw redirect({
        href: preserveSignedOut(`/sign-in-email?email=${encodeURIComponent(prefilledEmail)}`, signedOut),
      });
    }

    const redirectTo = url.searchParams.get('redirectTo');

    let authError: AuthErrors | undefined;
    if (appConfig) {
      const authSession = await useAuthSession();
      const sessionError = authSession.data.authError;
      if (sessionError) {
        authError = sessionError.message;
        await authSession.update({ authError: undefined });
      }
    }

    return {
      isSignupReady: appConfig
        ? appConfig.status.migrations && appConfig.status.hasOrg && appConfig.status.hasUser
        : false,
      authProviders,
      authMode,
      isEmailDiscoveryEnabled,
      prefilledEmail,
      didMigrationsRun: appConfig?.status.migrations ?? false,
      // A misconfigured provider list blocks every sign-in, so it wins over a leftover session error.
      authError: !appConfig
        ? ('BackendUnavailable' as AuthErrors)
        : !authMode
          ? ('InvalidAuthConfiguration' as AuthErrors)
          : authError,
      isManagedMarble: appConfig?.isManagedMarble ?? false,
      redirectTo,
      signedOut,
    };
  });

export const Route = createFileRoute('/_app/_auth/sign-in')({
  staticData: {
    i18n: authI18n,
  },
  validateSearch: signedOutPageSearch,
  loaderDeps: ({ search }) => ({ signedOut: search.signedOut === 1 }),
  loader: ({ deps }) => signInLoader({ data: deps }),
  component: Login,
  errorComponent: ErrorComponent,
});

function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const {
    isSignupReady,
    authProviders,
    authMode,
    isEmailDiscoveryEnabled,
    prefilledEmail,
    didMigrationsRun,
    isManagedMarble,
    authError,
    redirectTo,
    signedOut,
  } = Route.useLoaderData();

  const signInMutation = useMutation({
    mutationFn: async (authPayload: AuthPayload) =>
      signInFn({
        data: {
          idToken: authPayload.idToken,
          refreshToken: authPayload.refreshToken,
          csrf: authPayload.csrf,
          redirectTo: redirectTo ?? undefined,
        },
      }).then((r) => r.redirectTo),
    onSuccess: (destination) => {
      notifyOtherTabsToCheckSession();
      window.location.href = destination;
    },
  });

  const signIn = (authPayload: AuthPayload) => signInMutation.mutate(authPayload);
  const loading = signInMutation.isPending;
  const type = signInMutation.variables?.type;

  const [mfaChallenge, setMfaChallenge] = useState<{
    resolver: MultiFactorResolver;
    type: AuthPayload['type'];
  } | null>(null);

  if (!authMode) {
    return (
      <div className="flex flex-col gap-xl w-full">
        <Typo variant="title1" className="text-center">
          {t('auth:sign_in')}
        </Typo>
        <AuthError error={authError as AuthErrors} />
      </div>
    );
  }

  if (isEmailDiscoveryEnabled) {
    return (
      <div className="flex flex-col gap-xl w-full">
        {!isSignupReady ? <UnreadyCallout didMigrationsRun={didMigrationsRun} /> : null}
        <SignInWithEmailDiscovery prefilledEmail={prefilledEmail} redirectTo={redirectTo} signedOut={signedOut} />
        <AuthError error={authError as AuthErrors} />
      </div>
    );
  }

  if (mfaChallenge) {
    return (
      <div className="flex flex-col gap-2xl w-full">
        <MfaChallenge
          resolver={mfaChallenge.resolver}
          authType={mfaChallenge.type}
          signIn={signIn}
          loading={loading}
          onCancel={() => setMfaChallenge(null)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2xl w-full">
      <div className="flex flex-col gap-xl">
        <Typo variant="title1" className="text-center">
          {t('auth:sign_in')}
        </Typo>
        {!isSignupReady ? <UnreadyCallout didMigrationsRun={didMigrationsRun} /> : null}
        <div className="flex flex-col gap-sm">
          {authMode === 'oidc-legacy' ? (
            <button
              className="relative flex h-10 w-full items-center rounded border-2 border-[#1a73e8] bg-[#1a73e8] transition hover:bg-[rgb(69,128,233)] disabled:cursor-wait"
              onClick={() => {
                window.location.href = '/oidc/auth';
              }}
              disabled={loading}
            >
              <span className="text-s text-grey-white w-full whitespace-nowrap text-center align-middle font-medium">
                Sign in with OpenID Connect
              </span>
              <span className="absolute end-0 mx-sm size-4">{loading ? <Spinner className="size-4" /> : null}</span>
            </button>
          ) : null}
          {authProviders.includes('firebase') ? (
            <>
              <SignInWithGoogle
                signIn={signIn}
                onMfaRequired={(resolver) => setMfaChallenge({ resolver, type: 'google' })}
                loading={loading && type === 'google'}
              />
              <SignInWithMicrosoft
                signIn={signIn}
                onMfaRequired={(resolver) => setMfaChallenge({ resolver, type: 'microsoft' })}
                loading={loading && type === 'microsoft'}
              />
              <Link
                className={CtaV2ClassName({
                  variant: 'primary',
                  color: 'primary',
                  size: 'large',
                  className: 'w-full justify-center',
                })}
                to="/sign-in-email"
                search={signedOut ? { signedOut: 1 } : undefined}
              >
                {t('auth:sign_in.with_email')}
              </Link>
            </>
          ) : null}
          <AuthError error={authError as AuthErrors} className="mt-xl" />
        </div>
      </div>
      {authProviders.includes('firebase') ? (
        <>
          <div className="flex items-center gap-md self-stretch">
            <div className="h-px bg-grey-border grow" />
            <span className="text-purple-primary">{t('common:or')}</span>
            <div className="h-px bg-grey-border grow" />
          </div>
          <div className="flex flex-col gap-xl">
            <Typo variant="title1" as="h2" className="text-center">
              {t('auth:sign_in.first_connection')}
            </Typo>
            <div className="flex flex-col gap-sm">
              <SignInFirstConnection isSignInHomepage showAskDemoButton={isManagedMarble} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
